import {
  getGetCompanyListQueryKey,
  useCompaniesControllerUpdateDefaultOwner,
} from "@/spz-api/companies/companies";
import type { GetCompanyListItemResponseDto } from "@/spz-api/model";
import { useQueryClient } from "@tanstack/react-query";

export const useUpdateCompanyDefaultOwner = () => {
  const queryClient = useQueryClient();

  const { mutate } = useCompaniesControllerUpdateDefaultOwner({
    mutation: {
      onMutate: async (variables) => {
        const { companyId, data } = variables;

        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: getGetCompanyListQueryKey({}),
        });

        // Snapshot the previous value
        const previousData = queryClient.getQueryData<{
          data: GetCompanyListItemResponseDto[];
          total: number;
        }>(getGetCompanyListQueryKey({}));

        // Optimistically update to the new value
        queryClient.setQueryData<{
          data: GetCompanyListItemResponseDto[];
          total: number;
        }>(getGetCompanyListQueryKey({}), (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((company) => {
              if (company.id === companyId) {
                return {
                  ...company,
                  defaultOwner: data.newOwnerId,
                };
              }
              return company;
            }),
          };
        });

        // Return a context object with the snapshotted value
        return { previousData };
      },
      onError: (__, _, context) => {
        // Rollback to the previous value
        if (context?.previousData) {
          queryClient.setQueryData(
            getGetCompanyListQueryKey({}),
            context.previousData,
          );
        }
      },
      onSettled: () => {
        // Always refetch after error or success:
        queryClient.invalidateQueries({
          queryKey: getGetCompanyListQueryKey({}),
        });
      },
    },
  });

  return { mutate };
};
